import { useCallback, useEffect, useRef } from 'react';
import {
  getAngle,
  getDistance,
  getImageUnderPointer,
  getNormalizedAngle,
  getTargetPageImageToInsertAnnotation,
} from './previewUtils';
import { useAnnotations } from './useAnnotations';
import { AnnotationType, convertAnnotationDefinitionToAnnotationDefinitionCore } from '../../models/annotation.types';
import { fabric } from 'fabric';
import { useAnnotationsImageSetup, UseAnnotationImageSetupProps } from './useAnnotationsImageSetup';
import {
  ANNOTATION_DEFAULT_SCALE,
  ANNOTATION_OBJECT_NAME,
  DEFAULT_ANNOTATION_HEIGHT,
  DEFAULT_ANNOTATION_WIDTH,
} from './annotationsConsts';
import { pageImageData, Point } from './Preview.types';

export const useDynamicCreaton = ({ onAnnotationRightClick, onStoreAnnotation }: UseAnnotationImageSetupProps) => {
  const dynamicCreationMode = useRef<
    | {
        status: 'init' | 'created' | 'resize' | 'finish';
        annotationObject: fabric.Object | null;
        logicStartX?: number;
        logicStartY?: number;
        drawStartX?: number;
        drawStartY?: number;
        widthToHeightRatio?: number;
      }
    | undefined
  >(undefined);
  const { activeAnnotation, activeCriterion, canvas, getCanvasPointsForAnnotation, pageImages } = useAnnotations();

  const { createNewAnnotation, createAnnotation } = useAnnotationsImageSetup({
    onAnnotationRightClick,
    onStoreAnnotation,
    isAnnotationActive: !!activeAnnotation,
  });
  const isShiftKeyDown = useRef(false);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.shiftKey) {
      isShiftKeyDown.current = true;
    }
  }, []);

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (!event.shiftKey) {
      isShiftKeyDown.current = false;
    }
  }, []);

  const handleMouseDown = async (event: fabric.IEvent<MouseEvent>): Promise<boolean> => {
    if (
      dynamicCreationMode.current ||
      !activeCriterion ||
      activeAnnotation?.annotationType !== AnnotationType.dynamic
    ) {
      return false;
    }

    dynamicCreationMode.current = { status: 'init', annotationObject: null };

    let targetImageData = getTargetPageImageToInsertAnnotation(pageImages, event.target);
    let page: pageImageData | null = null;
    if (
      event.target?.name &&
      event.target.name.substring(0, ANNOTATION_OBJECT_NAME.length) === ANNOTATION_OBJECT_NAME
    ) {
      const pointer = canvas.getPointer(event.e);
      page = getImageUnderPointer(pageImages, pointer);
      if (!targetImageData && page) {
        targetImageData = page;
      }
    }
    if (!targetImageData) {
      return false;
    }

    const annotationDefinitionCore = convertAnnotationDefinitionToAnnotationDefinitionCore(activeAnnotation);

    const newAnnotation = await createNewAnnotation(
      event.e.x,
      event.e.y,
      targetImageData,
      annotationDefinitionCore,
      activeCriterion,
    );

    if (!newAnnotation) {
      return false;
    }

    const annotationImageData = await createAnnotation(newAnnotation, false);
    if (!annotationImageData?.image) {
      return false;
    }

    const image = annotationImageData.image;
    let originX;
    let originY;

    image.set({
      hasControls: false,
      selectable: false,
      ...(originX ? { originX } : {}),
      ...(originY ? { originY } : {}),
    });

    const canvasPoints = getCanvasPointsForAnnotation(event.e.x, event.e.y);
    dynamicCreationMode.current = {
      status: 'created',
      annotationObject: image,
      logicStartX: event.e.x,
      logicStartY: event.e.y,
      drawStartX: canvasPoints.x,
      drawStartY: canvasPoints.y,
      widthToHeightRatio: (image.width || DEFAULT_ANNOTATION_WIDTH) / (image.height || DEFAULT_ANNOTATION_HEIGHT),
    };

    // temporary annotation image has to be added to canvas
    canvas.add(image);
    canvas.bringToFront(image);

    window.addEventListener('keydown', handleKeyDown);
    return true;
  };

  const getDynamicCursorPosition = (x: number, y: number): Point => {
    const cursorX = x;
    const cursorY =
      isShiftKeyDown.current &&
      !activeAnnotation?.repetitive &&
      dynamicCreationMode.current?.logicStartY !== undefined &&
      dynamicCreationMode.current?.logicStartX !== undefined &&
      dynamicCreationMode.current?.widthToHeightRatio !== undefined
        ? dynamicCreationMode.current.logicStartY +
          Math.abs(
            (cursorX - dynamicCreationMode.current.logicStartX) / dynamicCreationMode.current.widthToHeightRatio,
          ) *
            (y > dynamicCreationMode.current.logicStartY ? 1 : -1)
        : y;

    return { x: cursorX, y: cursorY };
  };

  const handleMouseMove = (event: fabric.IEvent<MouseEvent>) => {
    if (
      !dynamicCreationMode.current ||
      !activeCriterion ||
      activeAnnotation?.annotationType !== AnnotationType.dynamic
    ) {
      return false;
    }

    const annotationImageDuringCreating = dynamicCreationMode.current?.annotationObject;
    const { drawStartX: startX, drawStartY: startY } = dynamicCreationMode.current;

    if (
      annotationImageDuringCreating &&
      startX &&
      startY &&
      annotationImageDuringCreating.left !== undefined &&
      annotationImageDuringCreating.top !== undefined &&
      annotationImageDuringCreating.width !== undefined &&
      annotationImageDuringCreating.height !== undefined &&
      annotationImageDuringCreating.scaleX !== undefined
    ) {
      const { x: cursorX, y: cursorY } = getDynamicCursorPosition(event.e.x, event.e.y);
      const canvasPoints = getCanvasPointsForAnnotation(cursorX, cursorY);
      const distance = getDistance(canvasPoints.x, canvasPoints.y, startX, startY);

      let newWidth;
      let newHeight;
      let newLeft;
      let newTop;
      let angle = 0;

      let finalWidth;
      let finalScaleX;
      let finalScaleY;

      if (activeAnnotation.annotationType === AnnotationType.dynamic) {
        newLeft = (startX + canvasPoints.x) / 2;
        newTop = (startY + canvasPoints.y) / 2;

        if (activeAnnotation.repetitive) {
          angle = getAngle(startX, startY, canvasPoints.x, canvasPoints.y);

          newWidth = distance / ANNOTATION_DEFAULT_SCALE;
          finalWidth = newWidth;
        } else {
          newWidth = canvasPoints.x - startX;
          newHeight = canvasPoints.y - startY;

          const oldWidth = annotationImageDuringCreating.width;
          const oldHeight = annotationImageDuringCreating.height;

          finalScaleX = newWidth / oldWidth;
          finalScaleY = newHeight / oldHeight;
        }
      }

      annotationImageDuringCreating.set({
        angle,
        ...(finalWidth ? { width: finalWidth } : {}),

        ...(finalScaleX ? { scaleX: finalScaleX } : {}),
        ...(finalScaleY ? { scaleY: finalScaleY } : {}),

        ...(newLeft ? { left: newLeft } : {}),
        ...(newTop ? { top: newTop } : {}),
      });

      canvas.renderAll();
    }
  };

  const handleMouseUp = (event: fabric.IEvent<MouseEvent>) => {
    if (
      !activeAnnotation ||
      !dynamicCreationMode.current?.annotationObject ||
      activeAnnotation?.annotationType !== AnnotationType.dynamic
    ) {
      return;
    }
    const { x: cursorX, y: cursorY } = getDynamicCursorPosition(event.e.x, event.e.y);

    const { width = 0, height = 0, scaleX = 1, scaleY = 1, angle = 0 } = dynamicCreationMode.current.annotationObject;

    let finalWidth;
    let finalHeight;

    const logicStartX = dynamicCreationMode.current.logicStartX || 0;
    const logicStartY = dynamicCreationMode.current.logicStartY || 0;

    if (dynamicCreationMode.current) {
      dynamicCreationMode.current = undefined;
    }

    if (activeAnnotation.annotationType === AnnotationType.dynamic && !activeAnnotation.repetitive) {
      finalWidth = (width * scaleX) / 2;
      finalHeight = (height * scaleY) / 2;
    } else {
      finalWidth = width;
      finalHeight = height;
    }

    window.removeEventListener('keydown', handleKeyDown);

    return {
      x: (logicStartX + cursorX) / 2,
      y: (logicStartY + cursorY) / 2,
      width: finalWidth,
      height: finalHeight,
      angle: getNormalizedAngle(angle),
    };
  };

  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyUp]);

  return {
    dynamicCreationMode,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
};
